// What a person had on screen when they left (the open tab, the filters, the problems they had opened), kept
// for the tab of the browser so that coming back to the results finds them as they were. It never leaves
// the browser, and every access is guarded: storage can be missing or refuse (private windows, blocked data).
import { useCallback, useState } from 'react';

export function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStored(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Without storage the state simply is not kept.
  }
}

/** State that is kept under `key` and read again when the key changes (another scan, another page). */
export function usePersisted<T>(key: string, initial: T) {
  const [state, setState] = useState(() => ({ key, value: readStored(key, initial) }));
  const current = state.key === key ? state.value : readStored(key, initial);
  if (state.key !== key) setState({ key, value: current });
  // `initial` is a constant of the caller: only the key makes the setter new.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above.
  const set = useCallback(
    (next: T | ((before: T) => T)) =>
      setState((before) => {
        const base = before.key === key ? before.value : readStored(key, initial);
        const value = typeof next === 'function' ? (next as (before: T) => T)(base) : next;
        writeStored(key, value);
        return { key, value };
      }),
    [key],
  );
  return [current, set] as const;
}
